import { useState } from "react";

const regions = [
  { flag: "🇺🇸", label: "USA", color: "bg-red-50 border-red-200 text-red-700" },
  { flag: "🇨🇦", label: "Canada", color: "bg-red-50 border-red-200 text-red-700" },
  { flag: "🇪🇺", label: "Europe", color: "bg-amber-50 border-amber-200 text-amber-700" },
  { flag: "🌎", label: "Latin America", color: "bg-green-50 border-green-200 text-green-700" },
  { flag: "🌙", label: "Middle East", color: "bg-orange-50 border-orange-200 text-orange-700" },
  { flag: "🌏", label: "Asia Pacific", color: "bg-gray-50 border-gray-200 text-gray-700" },
];

const recentQuotes = [
  {
    avatar: "CM",
    avatarBg: "bg-green-500",
    region: "🌎",
    country: "Mexico",
    product: "Organic Honey — 500 Units (Bulk Container)",
    category: "Honey & Sweeteners",
    quantity: "500 units / 1x 20ft Container",
    targetPrice: "$4.50/unit",
    port: "Manzanillo, Mexico",
    time: "2 hours ago",
    responses: 7,
  },
  {
    avatar: "SK",
    avatarBg: "bg-red-500",
    region: "🇺🇸",
    country: "USA",
    product: "Stainless Steel Kitchen Sets — 1,000 Units",
    category: "Home & Kitchen",
    quantity: "1,000 sets / 1x 40ft Container",
    targetPrice: "$28/set",
    port: "Los Angeles, CA",
    time: "4 hours ago",
    responses: 12,
  },
  {
    avatar: "AR",
    avatarBg: "bg-amber-500",
    region: "🌙",
    country: "UAE",
    product: "Premium Dates & Dried Fruits — 2 Containers",
    category: "Food & Beverages",
    quantity: "2x 40ft Containers",
    targetPrice: "$6,500/container",
    port: "Jebel Ali, Dubai",
    time: "6 hours ago",
    responses: 9,
  },
  {
    avatar: "EL",
    avatarBg: "bg-amber-600",
    region: "🇪🇺",
    country: "Germany",
    product: "Organic Wheat Flour — 20 Tons Bulk",
    category: "Grains & Flour",
    quantity: "20 Tons / LCL Shipment",
    targetPrice: "$380/ton",
    port: "Hamburg, Germany",
    time: "8 hours ago",
    responses: 15,
  },
  {
    avatar: "JT",
    avatarBg: "bg-red-700",
    region: "🇨🇦",
    country: "Canada",
    product: "Maple Syrup — 300 Cases (Full Container)",
    category: "Honey & Sweeteners",
    quantity: "300 cases / 1x 40ft Container",
    targetPrice: "$22/case",
    port: "Vancouver, BC",
    time: "10 hours ago",
    responses: 6,
  },
  {
    avatar: "PS",
    avatarBg: "bg-orange-500",
    region: "🌏",
    country: "India",
    product: "Cold-Pressed Olive Oil — 5,000 Bottles",
    category: "Oils & Condiments",
    quantity: "5,000 bottles / 1x 20ft Container",
    targetPrice: "$7.80/bottle",
    port: "Mumbai, India",
    time: "12 hours ago",
    responses: 11,
  },
];

const productCategories = [
  "Food & Beverages", "Farm Products", "Dairy & Eggs", "Grains & Flour",
  "Honey & Sweeteners", "Oils & Condiments", "Organic Products", "Frozen Foods",
  "Snacks & Nuts", "Beverages", "Health & Wellness", "Home & Kitchen",
  "Electronics", "Clothing & Apparel", "Industrial Goods", "Cosmetics & Beauty",
  "Furniture & Decor", "Toys & Baby Products", "Sports & Outdoors", "Other",
];

const containerTypes = [
  "20ft Container (~25 Tons)", "40ft Container (~28 Tons)", "40ft HC Container (~30 Tons)",
  "LCL (Less than Container Load)", "Air Freight", "Pallet / Truckload", "Custom Quantity",
];

const initialForm = {
  buyerName: "", email: "", phone: "", company: "", country: "", region: "",
  productName: "", category: "", quantity: "", containerType: "",
  targetPrice: "", deliveryPort: "", requirements: "", agreeTerms: false,
};

// ── Masked contact display ──────────────────────────────────────────────────
const MaskedContact = () => (
  <div className="flex items-center gap-1.5 mt-1.5">
    <div className="flex items-center gap-1 bg-gray-100 rounded px-2 py-0.5">
      <i className="ri-lock-line text-gray-400 text-[10px]"></i>
      <span className="text-gray-400 text-[10px] font-mono">●●●●●@●●●●.com</span>
    </div>
    <div className="flex items-center gap-1 bg-gray-100 rounded px-2 py-0.5">
      <i className="ri-lock-line text-gray-400 text-[10px]"></i>
      <span className="text-gray-400 text-[10px] font-mono">+●● ●●● ●●●●</span>
    </div>
  </div>
);

// ── Factory Membership Modal ────────────────────────────────────────────────
const MembershipModal = ({ onClose }: { onClose: () => void }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
    <div className="absolute inset-0 bg-black/60" onClick={onClose}></div>
    <div className="relative bg-white rounded-2xl w-full max-w-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#C41E3A] to-[#8B0000] p-6 text-white">
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/20 rounded-full cursor-pointer hover:bg-white/30 transition-colors">
          <i className="ri-close-line text-white"></i>
        </button>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-vip-crown-line text-[#D4AF37] text-xl"></i>
          </div>
          <div>
            <div className="font-extrabold text-lg">Verified Factory Membership</div>
            <div className="text-white/70 text-xs">Unlock all buyer contact details</div>
          </div>
        </div>
        <div className="mt-4 flex items-end gap-1">
          <span className="text-4xl font-extrabold text-[#D4AF37]">$8</span>
          <span className="text-white/70 text-sm mb-1">/month</span>
        </div>
        <div className="text-white/60 text-xs mt-1">Cancel anytime · No hidden fees</div>
      </div>

      {/* Benefits */}
      <div className="p-6">
        <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">What you get:</div>
        <div className="space-y-2.5 mb-6">
          {[
            { icon: "ri-eye-line", text: "Full buyer name, email & phone number revealed", color: "text-green-500" },
            { icon: "ri-mail-open-line", text: "Send quotations directly to buyer\'s email", color: "text-green-500" },
            { icon: "ri-notification-3-line", text: "Instant alerts for new buyer requirements", color: "text-green-500" },
            { icon: "ri-filter-3-line", text: "Filter buyers by region, category & quantity", color: "text-green-500" },
            { icon: "ri-verified-badge-line", text: "\"Verified Factory\" badge on your profile", color: "text-green-500" },
            { icon: "ri-customer-service-2-line", text: "Priority B2B support team access", color: "text-green-500" },
          ].map((b) => (
            <div key={b.text} className="flex items-start gap-2.5">
              <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                <i className={`${b.icon} ${b.color} text-sm`}></i>
              </div>
              <span className="text-gray-700 text-sm">{b.text}</span>
            </div>
          ))}
        </div>

        {/* Payment Methods */}
        <div className="bg-[#fdf8f4] border border-[#f0e8e0] rounded-xl p-4 mb-5">
          <div className="text-xs font-bold text-gray-600 mb-3 flex items-center gap-2">
            <i className="ri-secure-payment-line text-[#C41E3A]"></i>
            Accepted Payment Methods
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { icon: "ri-bank-card-line", label: "Credit Card", sub: "Visa / Mastercard / Amex" },
              { icon: "ri-bank-card-2-line", label: "Debit Card", sub: "All major banks" },
              { icon: "ri-paypal-line", label: "PayPal", sub: "Instant & secure" },
            ].map((p) => (
              <div key={p.label} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 flex-1 min-w-[120px]">
                <div className="w-7 h-7 flex items-center justify-center bg-[#C41E3A]/10 rounded-full shrink-0">
                  <i className={`${p.icon} text-[#C41E3A] text-sm`}></i>
                </div>
                <div>
                  <div className="text-xs font-bold text-gray-800">{p.label}</div>
                  <div className="text-[10px] text-gray-400">{p.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full bg-[#C41E3A] text-white py-3.5 rounded-xl font-extrabold text-sm hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
        >
          <i className="ri-vip-crown-line"></i>
          Subscribe Now — $8/month
        </button>
        <div className="flex items-center justify-center gap-4 mt-3">
          {["ri-visa-line", "ri-mastercard-line", "ri-paypal-line"].map((ic) => (
            <div key={ic} className="w-6 h-6 flex items-center justify-center">
              <i className={`${ic} text-gray-400 text-lg`}></i>
            </div>
          ))}
          <span className="text-gray-400 text-[10px]">256-bit SSL Encrypted</span>
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-lock-line text-gray-400 text-sm"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ── Main Component ──────────────────────────────────────────────────────────
const BuyerQuoteSection = () => {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [showMembership, setShowMembership] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      if (name === "requirements") {
        if (value.length > 500) return;
        setCharCount(value.length);
      }
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    if (!form.agreeTerms) {
      setError("Please agree to the privacy terms before submitting.");
      return;
    }
    setSubmitting(true);
    const body = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => body.append(k, String(v)));
    try {
      await fetch("https://readdy.ai/api/form/d7ddih9noju8nk5a193g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {showMembership && <MembershipModal onClose={() => setShowMembership(false)} />}

      <section id="buyer-quote" className="py-20 bg-gradient-to-b from-[#fdf8f4] to-white">
        <div className="max-w-[1400px] mx-auto px-6">

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#C41E3A]/10 text-[#C41E3A] text-xs font-bold px-4 py-2 rounded-full mb-4 uppercase tracking-widest">
              <i className="ri-global-line"></i> Global Bulk Buyer Portal
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
              Post Your Bulk Requirement &amp;<br />
              <span className="text-[#C41E3A]">Get Direct Factory Quotes</span>
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              Welcome buyers from <strong>USA, Canada, Europe, Latin America, Middle East &amp; Asia</strong>.
              Post your bulk container requirements — millions of verified factory &amp; farm bosses worldwide
              will send you the <strong>best quality products &amp; most competitive quotations</strong> directly to your email.
              Thank you very much for your cooperation!
            </p>
          </div>

          {/* Region Pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {regions.map((r) => (
              <div key={r.label} className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold ${r.color}`}>
                <span className="text-lg">{r.flag}</span>{r.label}
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
            {[
              { icon: "ri-building-2-line", value: "2M+", label: "Verified Factory Bosses", color: "text-[#C41E3A]" },
              { icon: "ri-global-line", value: "50+", label: "Countries Served", color: "text-amber-600" },
              { icon: "ri-mail-send-line", value: "24h", label: "Avg. Quote Response", color: "text-green-600" },
              { icon: "ri-lock-2-line", value: "100%", label: "Buyer Privacy Protected", color: "text-gray-600" },
            ].map((s) => (
              <div key={s.label} className="bg-white border border-gray-100 rounded-xl p-5 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-gray-50">
                  <i className={`${s.icon} text-xl ${s.color}`}></i>
                </div>
                <div className={`text-2xl font-extrabold ${s.color}`}>{s.value}</div>
                <div className="text-gray-500 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Privacy Notice Banner */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl px-6 py-4 mb-10 flex flex-col sm:flex-row items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37]/20 rounded-full shrink-0">
              <i className="ri-shield-keyhole-line text-[#D4AF37] text-xl"></i>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="text-white font-bold text-sm">Buyer Privacy Protection</div>
              <div className="text-gray-400 text-xs mt-0.5">
                Your name, email &amp; phone are <strong className="text-[#D4AF37]">completely hidden from the public</strong>.
                Only verified factory members who pay the <strong className="text-[#D4AF37]">$8/month membership fee</strong> can view your contact details and send you quotations.
              </div>
            </div>
            <button
              onClick={() => setShowMembership(true)}
              className="shrink-0 bg-[#D4AF37] text-[#1a0a0a] px-4 py-2 rounded-full text-xs font-extrabold hover:bg-[#c4a030] transition-colors cursor-pointer whitespace-nowrap"
            >
              Factory? Join $8/mo
            </button>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* ── Quote Form ── */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-gray-100 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#C41E3A]/10 rounded-full">
                    <i className="ri-file-list-3-line text-[#C41E3A] text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-extrabold text-gray-900 text-lg">Post Your Buying Requirement</h3>
                    <p className="text-gray-400 text-xs">Your contact info is private — only verified factories can see it</p>
                  </div>
                </div>

                {submitted ? (
                  <div className="text-center py-14">
                    <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
                      <i className="ri-check-double-line text-green-500 text-4xl"></i>
                    </div>
                    <h4 className="text-xl font-extrabold text-gray-900 mb-2">Requirement Posted Successfully!</h4>
                    <p className="text-gray-500 text-sm max-w-sm mx-auto leading-relaxed mb-5">
                      Thank you! Your bulk buying requirement is now live. Verified factory &amp; farm bosses
                      will send you the best competitive quotations directly to your email within 24 hours.
                    </p>
                    <div className="bg-[#fdf8f4] border border-[#f0e8e0] rounded-xl p-4 text-sm text-gray-600 mb-5 text-left">
                      <div className="flex items-start gap-2">
                        <i className="ri-shield-keyhole-line text-[#C41E3A] mt-0.5 shrink-0"></i>
                        <span>Your contact details are <strong>hidden from the public</strong>. Only verified factory members ($8/month) can view and contact you. Check your inbox for quotations!</span>
                      </div>
                    </div>
                    <button
                      onClick={() => { setSubmitted(false); setForm(initialForm); setCharCount(0); }}
                      className="bg-[#C41E3A] text-white px-6 py-3 rounded-full text-sm font-bold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Post Another Requirement
                    </button>
                  </div>
                ) : (
                  <form data-readdy-form id="buyer-quote-form" onSubmit={handleSubmit} className="space-y-4">

                    {/* Row 1 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">
                          Full Name <span className="text-[#C41E3A]">*</span>
                          <span className="ml-1 text-gray-400 font-normal">(hidden from public)</span>
                        </label>
                        <div className="relative">
                          <input type="text" name="buyerName" value={form.buyerName} onChange={handleChange} required
                            placeholder="Your full name"
                            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors pr-9" />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                            <i className="ri-lock-line text-gray-300 text-sm"></i>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">
                          Email Address <span className="text-[#C41E3A]">*</span>
                          <span className="ml-1 text-gray-400 font-normal">(hidden)</span>
                        </label>
                        <div className="relative">
                          <input type="email" name="email" value={form.email} onChange={handleChange} required
                            placeholder="your@email.com"
                            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors pr-9" />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                            <i className="ri-lock-line text-gray-300 text-sm"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Row 2 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">
                          Phone / WhatsApp
                          <span className="ml-1 text-gray-400 font-normal">(hidden)</span>
                        </label>
                        <div className="relative">
                          <input type="text" name="phone" value={form.phone} onChange={handleChange}
                            placeholder="+1 555 000 0000"
                            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors pr-9" />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                            <i className="ri-lock-line text-gray-300 text-sm"></i>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Company Name</label>
                        <input type="text" name="company" value={form.company} onChange={handleChange}
                          placeholder="Your company (optional)"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors" />
                      </div>
                    </div>

                    {/* Row 3 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Your Country <span className="text-[#C41E3A]">*</span></label>
                        <input type="text" name="country" value={form.country} onChange={handleChange} required
                          placeholder="e.g. USA, Canada, Germany..."
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Buying Region <span className="text-[#C41E3A]">*</span></label>
                        <select name="region" value={form.region} onChange={handleChange} required
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors bg-white cursor-pointer">
                          <option value="">Select your region</option>
                          <option value="USA">🇺🇸 USA</option>
                          <option value="Canada">🇨🇦 Canada</option>
                          <option value="Europe">🇪🇺 Europe</option>
                          <option value="Latin America">🌎 Latin America</option>
                          <option value="Middle East">🌙 Middle East</option>
                          <option value="Asia Pacific">🌏 Asia Pacific</option>
                          <option value="Africa">🌍 Africa</option>
                          <option value="Other">🌐 Other</option>
                        </select>
                      </div>
                    </div>

                    {/* Row 4 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Product Category <span className="text-[#C41E3A]">*</span></label>
                        <select name="category" value={form.category} onChange={handleChange} required
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors bg-white cursor-pointer">
                          <option value="">Select category</option>
                          {productCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Product Name <span className="text-[#C41E3A]">*</span></label>
                        <input type="text" name="productName" value={form.productName} onChange={handleChange} required
                          placeholder="e.g. Organic Honey, Kitchen Sets..."
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors" />
                      </div>
                    </div>

                    {/* Row 5 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Order Quantity <span className="text-[#C41E3A]">*</span></label>
                        <input type="text" name="quantity" value={form.quantity} onChange={handleChange} required
                          placeholder="e.g. 500 units, 2 containers, 10 tons"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Shipment Type</label>
                        <select name="containerType" value={form.containerType} onChange={handleChange}
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors bg-white cursor-pointer">
                          <option value="">Select shipment type</option>
                          {containerTypes.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Row 6 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Target Price (USD)</label>
                        <input type="text" name="targetPrice" value={form.targetPrice} onChange={handleChange}
                          placeholder="e.g. $5.00/unit or $8,000/container"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Destination Port / City</label>
                        <input type="text" name="deliveryPort" value={form.deliveryPort} onChange={handleChange}
                          placeholder="e.g. Los Angeles, Toronto, Hamburg..."
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors" />
                      </div>
                    </div>

                    {/* Requirements */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Special Requirements / Notes</label>
                      <textarea name="requirements" value={form.requirements} onChange={handleChange}
                        rows={4} maxLength={500}
                        placeholder="Certifications needed (USDA, FDA, Halal, Kosher, Organic), packaging, delivery timeline, quality standards..."
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors resize-none" />
                      <div className="text-right text-xs text-gray-400 mt-1">{charCount}/500</div>
                    </div>

                    {/* Privacy checkbox */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-start gap-3">
                      <input type="checkbox" name="agreeTerms" id="agreeTerms" checked={form.agreeTerms}
                        onChange={handleChange} className="mt-0.5 cursor-pointer accent-[#C41E3A]" />
                      <label htmlFor="agreeTerms" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
                        I understand my <strong>name, email &amp; phone are kept private</strong> and will only be shared with
                        verified factory members who pay the $8/month membership fee. I agree to the platform&apos;s
                        B2B buyer privacy policy.
                      </label>
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-600 text-xs px-4 py-3 rounded-lg">
                        <i className="ri-error-warning-line mr-2"></i>{error}
                      </div>
                    )}

                    <button type="submit" disabled={submitting}
                      className="w-full bg-[#C41E3A] text-white py-4 rounded-xl font-extrabold text-base hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-3">
                      {submitting ? (
                        <><i className="ri-loader-4-line animate-spin"></i> Submitting...</>
                      ) : (
                        <><i className="ri-send-plane-2-line"></i> Post My Bulk Requirement — Get Free Quotes</>
                      )}
                    </button>

                    <p className="text-center text-xs text-gray-400">
                      <i className="ri-shield-keyhole-line text-green-500 mr-1"></i>
                      100% Free for Buyers · Contact Info Hidden · Verified Factories Only · Quotes to Your Email
                    </p>
                  </form>
                )}
              </div>
            </div>

            {/* ── Right Panel ── */}
            <div className="lg:col-span-2 flex flex-col gap-6">

              {/* Factory Membership Card */}
              <div className="bg-gradient-to-br from-[#1a0a0a] to-[#3a0a0a] rounded-2xl p-6 text-white">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 flex items-center justify-center bg-[#D4AF37]/20 rounded-full">
                    <i className="ri-vip-crown-line text-[#D4AF37] text-base"></i>
                  </div>
                  <span className="font-extrabold text-sm text-[#D4AF37]">Verified Factory Membership</span>
                </div>
                <div className="flex items-end gap-1 mb-1 mt-3">
                  <span className="text-3xl font-extrabold text-white">$8</span>
                  <span className="text-white/60 text-sm mb-1">/month</span>
                </div>
                <p className="text-white/60 text-xs mb-4">Unlock full buyer contact details &amp; send quotations directly</p>
                <div className="space-y-2 mb-5">
                  {[
                    "View buyer name, email & phone",
                    "Send quotes directly to buyers",
                    "Instant new buyer alerts",
                    "Verified Factory badge",
                  ].map((f) => (
                    <div key={f} className="flex items-center gap-2 text-xs text-white/80">
                      <div className="w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-[#D4AF37]"></i>
                      </div>
                      {f}
                    </div>
                  ))}
                </div>
                {/* Payment icons */}
                <div className="flex gap-2 mb-4">
                  {[
                    { icon: "ri-bank-card-line", label: "Credit Card" },
                    { icon: "ri-bank-card-2-line", label: "Debit Card" },
                    { icon: "ri-paypal-line", label: "PayPal" },
                  ].map((p) => (
                    <div key={p.label} className="flex-1 bg-white/10 rounded-lg py-2 px-2 text-center">
                      <div className="w-5 h-5 flex items-center justify-center mx-auto mb-1">
                        <i className={`${p.icon} text-white/70 text-base`}></i>
                      </div>
                      <div className="text-[9px] text-white/50">{p.label}</div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setShowMembership(true)}
                  className="w-full bg-[#D4AF37] text-[#1a0a0a] py-3 rounded-xl font-extrabold text-sm hover:bg-[#c4a030] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-vip-crown-line mr-2"></i>Join Now — $8/month
                </button>
              </div>

              {/* How It Works */}
              <div className="bg-gradient-to-br from-[#C41E3A] to-[#8B0000] rounded-2xl p-6 text-white">
                <h4 className="font-extrabold text-sm mb-4 flex items-center gap-2">
                  <i className="ri-question-line"></i> How It Works
                </h4>
                <div className="space-y-3.5">
                  {[
                    { step: "1", title: "Buyer Posts Requirement", desc: "Fills in product, quantity & destination — contact info stays private" },
                    { step: "2", title: "Verified Factories See It", desc: "Only $8/month members can view full buyer contact details" },
                    { step: "3", title: "Factories Send Quotes", desc: "Best competitive quotations sent directly to buyer\'s email" },
                    { step: "4", title: "Deal Closed", desc: "Buyer compares quotes, negotiates & places bulk order" },
                  ].map((s) => (
                    <div key={s.step} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full text-xs font-extrabold shrink-0 mt-0.5">{s.step}</div>
                      <div>
                        <div className="font-bold text-xs">{s.title}</div>
                        <div className="text-white/60 text-[11px] leading-relaxed">{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Buyer Requests — masked */}
              <div className="bg-white border border-gray-100 rounded-2xl p-6">
                <h4 className="font-extrabold text-gray-900 text-sm mb-1 flex items-center gap-2">
                  <i className="ri-time-line text-[#C41E3A]"></i> Recent Buyer Requests
                </h4>
                <p className="text-gray-400 text-[10px] mb-4">
                  <i className="ri-lock-line mr-1"></i>Contact info visible to verified factory members only
                </p>
                <div className="space-y-4">
                  {recentQuotes.map((q) => (
                    <div key={q.avatar + q.country} className="border border-gray-100 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-7 h-7 flex items-center justify-center ${q.avatarBg} rounded-full text-white text-[10px] font-bold shrink-0`}>
                          {q.avatar}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-bold text-gray-900">{q.region} {q.country} Buyer</span>
                            <span className="bg-green-100 text-green-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full">{q.responses} quotes</span>
                          </div>
                          <MaskedContact />
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5 space-y-1">
                        <div className="text-xs font-bold text-gray-800">{q.product}</div>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                          <span className="text-[10px] text-gray-500"><i className="ri-stack-line mr-1"></i>{q.quantity}</span>
                          <span className="text-[10px] text-gray-500"><i className="ri-price-tag-3-line mr-1"></i>{q.targetPrice}</span>
                          <span className="text-[10px] text-gray-500"><i className="ri-map-pin-line mr-1"></i>{q.port}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowMembership(true)}
                        className="mt-2 w-full text-[10px] font-bold text-[#C41E3A] border border-[#C41E3A]/30 rounded-lg py-1.5 hover:bg-[#C41E3A]/5 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
                      >
                        <i className="ri-lock-unlock-line"></i> Unlock Contact — Join $8/month
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA for Suppliers */}
          <div className="mt-14 bg-gradient-to-r from-[#1a0a0a] to-[#3a0a0a] rounded-2xl p-8 text-center text-white">
            <div className="text-3xl mb-3">🌍</div>
            <h3 className="text-2xl font-extrabold mb-2">Are You a Factory or Farm Supplier?</h3>
            <p className="text-white/70 text-sm max-w-xl mx-auto mb-3 leading-relaxed">
              Join millions of verified suppliers. Pay just <strong className="text-[#D4AF37]">$8/month</strong> to unlock
              buyer contact details and send quotations directly. Accept Credit Card, Debit Card &amp; PayPal.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {["Credit Card", "Debit Card", "PayPal"].map((pm) => (
                <span key={pm} className="bg-white/10 text-white/70 text-xs px-3 py-1 rounded-full border border-white/20 font-semibold">{pm}</span>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => setShowMembership(true)}
                className="flex items-center gap-2 bg-[#D4AF37] text-[#1a0a0a] px-6 py-3 rounded-full font-extrabold text-sm hover:bg-[#c4a030] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-vip-crown-line"></i> Join Verified Factory — $8/month
              </button>
              <a href="https://wa.me/1234567890" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full font-extrabold text-sm hover:bg-[#1da851] transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-whatsapp-line"></i> WhatsApp Us
              </a>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default BuyerQuoteSection;
